// THE RENEWAL ANSWER — a never-sum composition over the attribution ledger.
//
// "What has Helix found and delivered this year?" is the AI-ROI question that
// justifies renewals. It is answered here as a rollup over the create-only
// attribution ledger, obeying the never-sum honesty law:
//
//   - CLEAN attributed outcomes (the action cleanly owns the window) sum into the
//     governed realized total and its Conservative/Base/Stretch attribution band.
//   - CONFOUNDED-and-declared outcomes are held on a SEPARATE line and are NEVER
//     summed into the realized total. A confounded window cannot be claimed as
//     clean delivery; it is reported as a range, honestly, next to the clean total.
//
// Every contributing outcome appears as a both-direction row (rollup <-> the
// commitment that promised the action <-> the actuals that proved it), so the
// headline is always drillable back to its governed evidence.
//
// (Cross-commitment footprint overlap — two clean levers drawing the same cost
// pool — is a separate never-double-count concern owned by the governed-tool-output
// overlap resolver; the confound layer here has already routed temporally
// overlapping actions into the confounded line, so summing the remaining CLEAN,
// distinct-commitment base dollars is honest.)

import type { AttributionBand } from './attribution-engine';
import type {
  AttributionLedgerEntry,
  AttributionLedgerEntryStatus,
} from './attribution-ledger';

export const ATTRIBUTION_ROLLUP_SCHEMA_VERSION = 'attribution_rollup_v1' as const;
export type AttributionRollupSchemaVersion = typeof ATTRIBUTION_ROLLUP_SCHEMA_VERSION;

export type AttributionRollupRow = Readonly<{
  entryId: string;
  commitmentId: string;
  decisionId: string;
  metricKey: string;
  actualsSourceArtifactId: string;
  asOf: string;
  attributedBaseUsd: number;
  attributedBandUsd: AttributionBand;
  status: AttributionLedgerEntryStatus;
}>;

export type AttributionRollupLine = Readonly<{
  count: number;
  bandUsd: AttributionBand;
  rows: readonly AttributionRollupRow[];
}>;

export type AttributionRollup = Readonly<{
  schemaVersion: AttributionRollupSchemaVersion;
  asOfRange: Readonly<{ from: string; to: string }> | null;
  // Clean line — the only line that sums into the realized total.
  cleanAttributed: AttributionRollupLine;
  // Confounded-and-declared line — held separately, never summed into realized.
  confoundedDeclared: AttributionRollupLine;
  totalRealizedBaseUsd: number;
  attributionBandUsd: AttributionBand;
  commitmentCount: number;
  confoundedCount: number;
  renewalAnswer: string;
}>;

export function composeAttributionRollup(
  ledger: readonly AttributionLedgerEntry[],
): AttributionRollup {
  const entries = ledger ?? [];
  const cleanRows: AttributionRollupRow[] = [];
  const confoundedRows: AttributionRollupRow[] = [];
  let minAsOf: string | null = null;
  let maxAsOf: string | null = null;

  for (const entry of entries) {
    const row = rowFromEntry(entry);
    if (entry.entryStatus === 'confounded') {
      confoundedRows.push(row);
    } else {
      cleanRows.push(row);
    }
    if (minAsOf === null || row.asOf < minAsOf) minAsOf = row.asOf;
    if (maxAsOf === null || row.asOf > maxAsOf) maxAsOf = row.asOf;
  }

  const cleanAttributed: AttributionRollupLine = Object.freeze({
    count: cleanRows.length,
    bandUsd: sumBand(cleanRows),
    rows: Object.freeze(cleanRows),
  });
  const confoundedDeclared: AttributionRollupLine = Object.freeze({
    count: confoundedRows.length,
    bandUsd: sumBand(confoundedRows),
    rows: Object.freeze(confoundedRows),
  });

  const attributionBandUsd = cleanAttributed.bandUsd;
  const asOfRange =
    minAsOf !== null && maxAsOf !== null
      ? Object.freeze({ from: minAsOf, to: maxAsOf })
      : null;

  return Object.freeze({
    schemaVersion: ATTRIBUTION_ROLLUP_SCHEMA_VERSION,
    asOfRange,
    cleanAttributed,
    confoundedDeclared,
    totalRealizedBaseUsd: attributionBandUsd.baseUsd,
    attributionBandUsd,
    commitmentCount: cleanAttributed.count,
    confoundedCount: confoundedDeclared.count,
    renewalAnswer: buildRenewalAnswer({
      totalRealizedBaseUsd: attributionBandUsd.baseUsd,
      band: attributionBandUsd,
      commitmentCount: cleanAttributed.count,
      confoundedCount: confoundedDeclared.count,
      confoundedBand: confoundedDeclared.bandUsd,
    }),
  });
}

function rowFromEntry(entry: AttributionLedgerEntry): AttributionRollupRow {
  return Object.freeze({
    entryId: entry.entryId,
    commitmentId: entry.commitmentRef.commitmentId,
    decisionId: entry.commitmentRef.decisionId,
    metricKey: entry.outcome.metricKey,
    actualsSourceArtifactId: entry.actualsRef.sourceArtifactId,
    asOf: entry.actualsRef.asOf,
    attributedBaseUsd: entry.outcome.attributedImpact.baseUsd,
    attributedBandUsd: entry.outcome.attributedImpact,
    status: entry.entryStatus,
  });
}

function sumBand(rows: readonly AttributionRollupRow[]): AttributionBand {
  let conservativeUsd = 0;
  let baseUsd = 0;
  let stretchUsd = 0;
  for (const row of rows) {
    conservativeUsd += row.attributedBandUsd.conservativeUsd;
    baseUsd += row.attributedBandUsd.baseUsd;
    stretchUsd += row.attributedBandUsd.stretchUsd;
  }
  return Object.freeze({
    conservativeUsd: round(conservativeUsd, 2),
    baseUsd: round(baseUsd, 2),
    stretchUsd: round(stretchUsd, 2),
  });
}

function buildRenewalAnswer(params: {
  totalRealizedBaseUsd: number;
  band: AttributionBand;
  commitmentCount: number;
  confoundedCount: number;
  confoundedBand: AttributionBand;
}): string {
  const dollars = (usd: number): string =>
    `${usd < 0 ? '-' : ''}$${Math.abs(usd).toLocaleString('en-US')}`;
  const base = params.commitmentCount === 0
    ? 'Helix-governed action has no clean attributed delivery on the ledger yet'
    : `Helix-governed action delivered ${dollars(params.totalRealizedBaseUsd)} across ` +
      `${params.commitmentCount} commitment${params.commitmentCount === 1 ? '' : 's'} ` +
      `(${dollars(params.band.conservativeUsd)}–${dollars(params.band.stretchUsd)} attribution band, governed)`;
  const confounded = params.confoundedCount === 0
    ? '0 confounded outcomes'
    : `${params.confoundedCount} outcome${params.confoundedCount === 1 ? '' : 's'} confounded-and-declared ` +
      `(${dollars(params.confoundedBand.conservativeUsd)}–${dollars(params.confoundedBand.stretchUsd)}, ` +
      `reported as ranges, held separately — never summed into realized)`;
  return `${base}; ${confounded}.`;
}

function round(value: number, digits: number): number {
  if (!Number.isFinite(value)) return value;
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function getAttributionRollupImplementationDigestInputs(): readonly Readonly<{
  name: string;
  source: string;
}>[] {
  return Object.freeze([
    { name: 'composeAttributionRollup', source: String(composeAttributionRollup) },
    { name: 'rowFromEntry', source: String(rowFromEntry) },
    { name: 'sumBand', source: String(sumBand) },
    { name: 'buildRenewalAnswer', source: String(buildRenewalAnswer) },
    { name: 'round', source: String(round) },
  ]);
}
